/**
 * Starter ideas for service packages. A specialist picks one, then edits it
 * to match their own background — nothing is published as-is.
 * `hours` is used to suggest a price from the specialist's hourly rate.
 */

const COMMON = {
  uk: [
    {
      key: "defense",
      title: "Підготовка до захисту дипломної / курсової",
      audience: "Студенти бакалаврату та магістратури",
      outcomes: ["Розбір структури роботи та слабких місць", "Пробний захист із типовими питаннями комісії", "Поради щодо презентації"],
      format: "single", durationMin: 60, hours: 1,
      requirements: "Текст роботи або чернетка презентації",
    },
    {
      key: "review",
      title: "Рецензія наукової статті або роботи",
      audience: "Аспіранти, науковці, студенти",
      outcomes: ["Письмовий відгук зі списком правок", "Оцінка методології та структури", "Рекомендації щодо доопрацювання"],
      format: "async", deliveryDays: 5, hours: 2,
      requirements: "Текст у форматі Word або PDF",
    },
    {
      key: "team_training",
      title: "Тренінг для команди",
      audience: "Компанії та команди до 15 осіб",
      outcomes: ["Програма під задачі вашої команди", "Практичні вправи на реальних кейсах", "Матеріали після тренінгу"],
      format: "single", durationMin: 120, hours: 2,
      requirements: "Короткий опис команди та цілей",
    },
    {
      key: "mentoring",
      title: "Менторська програма (4 зустрічі)",
      audience: "Ті, хто хоче системно розвинутись у темі",
      outcomes: ["Індивідуальний план розвитку", "4 зустрічі по 60 хв", "Домашні завдання та зворотний зв'язок"],
      format: "multi", sessions: 4, durationMin: 60, hours: 4,
      requirements: "",
    },
  ],
  en: [
    {
      key: "defense",
      title: "Thesis / coursework defense preparation",
      audience: "Bachelor's and master's students",
      outcomes: ["Review of the structure and weak points", "Mock defense with typical committee questions", "Presentation tips"],
      format: "single", durationMin: 60, hours: 1,
      requirements: "The thesis text or a draft presentation",
    },
    {
      key: "review",
      title: "Review of a research paper or thesis",
      audience: "PhD students, researchers, students",
      outcomes: ["Written feedback with a list of edits", "Assessment of methodology and structure", "Recommendations for revision"],
      format: "async", deliveryDays: 5, hours: 2,
      requirements: "Text in Word or PDF",
    },
    {
      key: "team_training",
      title: "Team training",
      audience: "Companies and teams of up to 15 people",
      outcomes: ["Program tailored to your team's goals", "Hands-on exercises on real cases", "Materials after the training"],
      format: "single", durationMin: 120, hours: 2,
      requirements: "A short description of the team and goals",
    },
    {
      key: "mentoring",
      title: "Mentoring program (4 sessions)",
      audience: "Anyone who wants to grow in the topic systematically",
      outcomes: ["Personal development plan", "4 sessions of 60 min", "Homework and feedback"],
      format: "multi", sessions: 4, durationMin: 60, hours: 4,
      requirements: "",
    },
  ],
};

const BY_SPHERE = {
  marketing: {
    uk: { title: "Аудит маркетингової стратегії", audience: "Малий та середній бізнес", outcomes: ["Аналіз поточних каналів і показників", "Список пріоритетних покращень", "План дій на 3 місяці"], format: "async", deliveryDays: 7, hours: 3, requirements: "Доступ до звітів або опис поточного маркетингу" },
    en: { title: "Marketing strategy audit", audience: "Small and medium businesses", outcomes: ["Analysis of current channels and metrics", "Prioritized list of improvements", "3-month action plan"], format: "async", deliveryDays: 7, hours: 3, requirements: "Access to reports or a description of current marketing" },
  },
  law: {
    uk: { title: "Юридичний аналіз договору", audience: "ФОП, малий бізнес, фізособи", outcomes: ["Аналіз ризиків у договорі", "Коментарі та пропозиції правок", "Відповіді на питання за 30-хв дзвінок"], format: "async", deliveryDays: 3, hours: 2, requirements: "Текст договору" },
    en: { title: "Legal review of a contract", audience: "Sole proprietors, small businesses, individuals", outcomes: ["Risk analysis of the contract", "Comments and suggested edits", "Q&A in a 30-min call"], format: "async", deliveryDays: 3, hours: 2, requirements: "Contract text" },
  },
  economics: {
    uk: { title: "Аналіз бізнес-плану", audience: "Стартапи та підприємці", outcomes: ["Перевірка економічних припущень", "Оцінка ризиків і точки беззбитковості", "Письмові рекомендації"], format: "async", deliveryDays: 5, hours: 3, requirements: "Бізнес-план або фінансова модель" },
    en: { title: "Business plan review", audience: "Startups and entrepreneurs", outcomes: ["Check of economic assumptions", "Risk and break-even assessment", "Written recommendations"], format: "async", deliveryDays: 5, hours: 3, requirements: "Business plan or financial model" },
  },
  finance: {
    uk: { title: "Побудова фінансової моделі", audience: "Підприємці та фінансові менеджери", outcomes: ["Модель доходів і витрат у таблиці", "Сценарії: песимістичний / базовий / оптимістичний", "Розбір моделі на дзвінку"], format: "multi", sessions: 2, durationMin: 60, hours: 4, requirements: "Вихідні дані про бізнес" },
    en: { title: "Financial model build", audience: "Entrepreneurs and finance managers", outcomes: ["Revenue and cost model in a spreadsheet", "Pessimistic / base / optimistic scenarios", "Walkthrough call"], format: "multi", sessions: 2, durationMin: 60, hours: 4, requirements: "Business input data" },
  },
  it: {
    uk: { title: "Технічне рев'ю проєкту", audience: "Розробники та стартапи", outcomes: ["Рев'ю архітектури та коду", "Список ризиків і технічного боргу", "Рекомендації на 60-хв дзвінку"], format: "single", durationMin: 60, hours: 2, requirements: "Доступ до репозиторію або опис архітектури" },
    en: { title: "Technical project review", audience: "Developers and startups", outcomes: ["Architecture and code review", "List of risks and tech debt", "Recommendations in a 60-min call"], format: "single", durationMin: 60, hours: 2, requirements: "Repository access or an architecture description" },
  },
  ai: {
    uk: { title: "Впровадження AI у робочі процеси", audience: "Команди та малий бізнес", outcomes: ["Аналіз процесів, які можна автоматизувати", "Підбір AI-інструментів", "Покроковий план впровадження"], format: "multi", sessions: 2, durationMin: 60, hours: 3, requirements: "Опис поточних процесів" },
    en: { title: "Bringing AI into workflows", audience: "Teams and small businesses", outcomes: ["Analysis of processes to automate", "Selection of AI tools", "Step-by-step rollout plan"], format: "multi", sessions: 2, durationMin: 60, hours: 3, requirements: "Description of current processes" },
  },
  psychology: {
    uk: { title: "Супервізія для практикуючих психологів", audience: "Психологи-початківці", outcomes: ["Розбір випадку з практики", "Професійний зворотний зв'язок", "Рекомендації щодо подальшої роботи"], format: "single", durationMin: 60, hours: 1, requirements: "Анонімізований опис випадку" },
    en: { title: "Supervision for practicing psychologists", audience: "Early-career psychologists", outcomes: ["Case discussion", "Professional feedback", "Recommendations for further work"], format: "single", durationMin: 60, hours: 1, requirements: "Anonymized case description" },
  },
  scientific_research: {
    uk: { title: "Підготовка статті до публікації (Scopus / WoS)", audience: "Аспіранти та науковці", outcomes: ["Підбір журналу", "Редагування структури та анотації", "Перевірка відповідності вимогам журналу"], format: "async", deliveryDays: 10, hours: 4, requirements: "Чернетка статті" },
    en: { title: "Preparing a paper for publication (Scopus / WoS)", audience: "PhD students and researchers", outcomes: ["Journal selection", "Editing structure and abstract", "Check against journal requirements"], format: "async", deliveryDays: 10, hours: 4, requirements: "Draft paper" },
  },
};

/** Templates for a specialist: sphere-specific first, then common ones. */
export function getPackageTemplates(sphere, lang = "uk") {
  const L = lang === "en" ? "en" : "uk";
  const list = [];
  const specific = BY_SPHERE[sphere]?.[L];
  if (specific) list.push({ key: `sphere_${sphere}`, ...specific });
  return list.concat(COMMON[L]);
}

/** Suggested price: hourly rate × estimated hours, rounded to 50 ₴. */
export function suggestPrice(rate, hours) {
  const r = Number(rate);
  if (!Number.isFinite(r) || r <= 0 || !hours) return "";
  return Math.max(50, Math.round((r * hours) / 50) * 50);
}

/** Short human summary of a package's format, e.g. "4 зустрічі × 60 хв". */
export function formatPackageMeta(t, p) {
  if (!p) return "";
  if (p.format === "async") {
    return t("packages.meta.async", { days: p.deliveryDays || 7 });
  }
  if (p.format === "multi") {
    return t("packages.meta.multi", { n: p.sessions || 2, min: p.durationMin || 60 });
  }
  return t("packages.meta.single", { min: p.durationMin || 60 });
}
